import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, ShieldCheck, Award, Heart, Star, Users, Sparkles, ChevronLeft, ChevronRight, Volume2, VolumeX, Newspaper } from "lucide-react";
import { COURSES } from "../lib/data";
import { useSEO } from "../lib/useSEO";
import { getActiveBreak } from "../lib/vdCalendar";
import { BlobShape } from "../components/decor/BlobShape";
import { DotsPattern } from "../components/decor/DotsPattern";
import { WaveDecor } from "../components/decor/WaveDecor";
import { useRevealOnScroll } from "../lib/useReveal";
import { thumb, thumbSet, fallbackToFull } from "../lib/imageThumb";
import { annonceActive, fenetreEnCours, prochaineFenetre, libelleFenetreCourt, titreAnnonce } from "../lib/dates";
import { useReglages } from "../lib/useReglages";
import { SpectacleCountdown } from "../components/SpectacleCountdown";

const AUDIENCES = [
  { key: "enfants", label: "Enfants", sub: "4 - 12 ans",
    img: "/img/be6208eca7.webp" },
  { key: "ados", label: "Ados", sub: "13 - 17 ans",
    img: "/img/7367a38920.webp" },
  { key: "adultes", label: "Adultes", sub: "18 ans et +",
    img: "/img/9eb6cf8f9e.webp" },
];

const TRUST = [
  { icon: GraduationCap, label: "Professeurs diplômés" },
  { icon: ShieldCheck, label: "Environnement sécurisant" },
  { icon: Award, label: "École reconnue" },
  { icon: Heart, label: "Suivi personnalisé" },
];

const MARQUEE_ITEMS = ["HIP-HOP", "BALLET", "COMÉDIE MUSICALE", "DANCEHALL", "HEELS", "SALSA", "ZUMBA", "THÉÂTRE", "CHANT"];

// Presse — mise en avant sur l'accueil. Les extraits sont volontairement différents de
// ceux de la page Actualités pour ne pas dupliquer le contenu aux yeux de Google.
const PRESS = [
  {
    key: "reflets",
    outlet: "Reflets de Cugy",
    kicker: "Journal de Cugy",
    date: "Octobre 2025",
    title: "« 1000 Arts : tous en scène ! »",
    excerpt: "Qu'est-ce qui a amené ce couple de passionnés à s'installer à Cugy ? « Tout nous ramène à Cugy. »",
    img: "/img/2074c5b565.webp",
    href: "/pdf/reflets-de-cugy-1000arts.pdf",
    cta: "Lire l'article (PDF)",
  },
  {
    key: "agenda",
    outlet: "L'Agenda Romandie",
    kicker: "Culture romande",
    date: "Février 2025",
    title: "« Deux énergies solaires et 1000 Arts »",
    excerpt: "Une école de danse ouvre ses portes à Cugy. À sa tête : deux artistes idéalistes et déterminés.",
    img: "/img/b87b5ea49a.webp",
    href: "https://www.l-agenda.ch/1000-arts/",
    cta: "Lire l'article",
  },
  {
    key: "echo",
    outlet: "L'Écho du Gros-de-Vaud",
    kicker: "Presse régionale",
    date: "Février 2025",
    title: "« Influence new-yorkaise à l'École 1000 Arts »",
    excerpt: "Le journal du district consacre deux articles à l'ouverture de l'école et à sa signature new-yorkaise.",
    img: "/img/14e228bd26.webp",
    href: "https://www.instagram.com/p/DFqQD0EoxD3",
    cta: "Voir l'article",
  },
];

const REVIEWS = [
  { name: "Claudio Falcetta", initial: "C", color: "#3B82F6", time: "il y a 4 mois", text: "Chez 1000 Arts, les profs ne se contentent pas d'enseigner un art. Ils partagent une passion, un message profond et une culture. Miller ne m'a pas juste appris à danser. Il m'a appris à m'exprimer avec mon corps et je lui serai à jamais reconnaissant pour ça." },
  { name: "La Fleur", initial: "L", color: "#7C3AED", time: "il y a un mois", text: "Noémie et Miller sont tout simplement géniaux. Nous sommes allés les voir pour notre première danse de mariage. De vrais rayons de soleils ! L'ambiance conviviale et la safe place qu'ils ont su créer m'ont permis de me lâcher et d'apprécier le moment. Je vous les recommande les yeux fermés !" },
  { name: "Nathalie Pari", initial: "N", color: "#0EA5E9", time: "il y a 5 mois", text: "Une super équipe, ils sont à l'écoute et tellement bienveillants. Ils arrivent à mettre en confiance et surtout à mettre en lumière. Ma fille fait aussi des cours avec eux et elle est enchantée." },
  { name: "Sarah Pillonel", initial: "S", color: "#F97316", time: "il y a un mois", text: "Une expérience magique dans cette école. Des profs talentueux, bienveillants et passionnés qui transmettent leur énergie et leur savoir avec le cœur." },
  { name: "Fanny Orlando", initial: "F", color: "#A16207", time: "il y a un an", text: "L'école 1000 ARTS est un lieu plein de vie et d'énergie positive. Merci à Miller et Noémie pour leur bienveillance, leur passion et leur capacité à faire briller les élèves. On se sent accueilli, encouragé, et porté à donner le meilleur de soi. Je recommande de tout cœur." },
  { name: "Andrea Paquier", initial: "A", color: "#475569", time: "il y a 5 mois", text: "L'école 1000 ARTS est une superbe école de danse avec des profs incroyables ! Noémie et Miller sont bienveillants, stimulants et très doués pour transmettre leur passion. Je recommande vivement ❤️" },
];

function ReviewsCarousel() {
  const [start, setStart] = useState(0);
  const visible = 3;
  const next = () => setStart((s) => (s + visible) % REVIEWS.length);
  const prev = () => setStart((s) => (s - visible + REVIEWS.length) % REVIEWS.length);
  const shown = [0, 1, 2].map((i) => REVIEWS[(start + i) % REVIEWS.length]);
  return (
    <div data-testid="reviews-carousel">
      <div className="grid md:grid-cols-3 gap-5">
        {shown.map((r, i) => (
          <a
            key={`${start}-${i}`}
            href="https://share.google/nVaMuIAnVhVVLcWys"
            target="_blank"
            rel="noreferrer"
            className="card-flat block hover:shadow-lg transition-shadow"
            data-testid={`review-card-${i}`}
            aria-label={`Lire l'avis de ${r.name} sur Google`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-semibold" style={{ background: r.color }}>{r.initial}</div>
              <div className="flex-1">
                <div className="font-medium text-sm">{r.name}</div>
                <div className="text-xs text-[var(--muted)]">{r.time}</div>
              </div>
              <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.997 10.997 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18a10.997 10.997 0 0 0 0 9.86l2.83-2.2.83-.64z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            </div>
            <div className="flex gap-0.5 mb-3">{[1,2,3,4,5].map((s) => <Star key={s} size={14} style={{ color: "#FFC107", fill: "#FFC107" }} />)}</div>
            <p className="text-sm text-[var(--text)] leading-relaxed">{r.text}</p>
          </a>
        ))}
      </div>
      <div className="flex items-center justify-between mt-6">
        <a href="https://share.google/nVaMuIAnVhVVLcWys" target="_blank" rel="noreferrer" data-testid="reviews-link" className="btn-outline">
          Voir tous les avis sur Google <ArrowRight size={14} />
        </a>
        <div className="flex gap-2">
          <button onClick={prev} data-testid="review-prev" className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}><ChevronLeft size={18} /></button>
          <button onClick={next} data-testid="review-next" className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--primary)", color: "#fff" }}><ChevronRight size={18} /></button>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  // Dates des portes ouvertes : saisies par l'école dans le classeur (onglet Listes).
  // La meta description suit donc la prochaine fenêtre, sans jamais être périmée.
  const { portes_ouvertes: fenetresPO, eleves_actifs: nbEleves } = useReglages();
  const fenetrePO = prochaineFenetre(fenetresPO);
  const phrasePO = fenetrePO
    ? `Portes ouvertes ${libelleFenetreCourt(fenetrePO)} — cours d'essai offerts.`
    : "Cours d'essai offerts pendant nos Portes Ouvertes.";
  useSEO({
    title: "1000 ARTS — École de danse à Cugy VD · Hip-hop, comédie musicale, ballet",
    description: `École de danse, chant et théâtre à Cugy VD, à 10 min de Lausanne. Hip-hop, comédie musicale, ballet, heels, salsa, zumba. Enfants dès 2 ans, ados et adultes. ${phrasePO}`,
    canonical: "/",
  });
  useRevealOnScroll();
  // Note et nombre d'avis lus chez Google par le serveur (au maximum 2 fois par jour).
  // Valeurs de repli affichées si l'appel échoue : la page ne doit jamais se casser pour ça.
  const [noteGoogle, setNoteGoogle] = useState({ rating: 5, count: 109 });
  useEffect(() => {
    let annule = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/google-reviews`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!annule && d && d.rating && d.count) setNoteGoogle({ rating: d.rating, count: d.count });
      })
      .catch(() => {});
    return () => { annule = true; };
  }, []);
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section data-testid="hero" className="relative pt-32 pb-20 overflow-hidden mesh-bg">
        <div className="absolute -top-10 -right-20 w-[600px] h-[600px] hero-shape" style={{ background: "var(--primary-soft)" }} />
        <div className="absolute top-40 -left-20 w-[300px] h-[300px] hero-shape" style={{ opacity: 0.2 }} />
        {/* Décors "Mouvement & Fluidité" */}
        <BlobShape variant="soft" size="lg" position="top-60 -left-32" opacity={0.18} animation="float" />
        <DotsPattern position="bottom-10 left-1/3" size={180} opacity={0.15} />
        <WaveDecor className="absolute bottom-0 left-0 w-full h-20" variant="primary" stroke={1.5} animate={true} />

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            {annonceActive(fenetresPO) && (
              <Link
                to="/inscription"
                data-testid="hero-rentree-badge"
                className="inline-flex items-center gap-2 px-4 py-1.5 mb-5 rounded-full text-sm font-semibold transition-transform hover:scale-[1.03]"
                style={{ background: "var(--primary-tint)", color: "var(--primary)" }}
              >
                <span
                  className="w-2 h-2 rounded-full animate-pulse"
                  style={{ background: "var(--primary)" }}
                  aria-hidden="true"
                />
                {fenetreEnCours(fenetresPO) ? "Portes ouvertes — cours d'essai offerts" : titreAnnonce()}
              </Link>
            )}
            <div className="eyebrow mb-5">École de Danse · Cugy VD</div>
            <h1 className="font-display text-5xl md:text-7xl leading-[1.05] tracking-tight">
              Danse, chante,<br /><span style={{ color: "var(--primary)" }}>vis</span> !
            </h1>
            <p className="mt-6 text-lg text-[var(--muted)] max-w-xl leading-relaxed">
              Du hip-hop à la comédie musicale, viens vibrer avec nous !
            </p>
            <div className="mt-8 flex items-start gap-4 max-w-xl" data-testid="hero-founder-quote">
              <img loading="eager" fetchPriority="high" decoding="async" width="80" height="80"
                src="/miller-face.jpg"
                alt="Miller Sacon, fondateur de 1000 ARTS"
                className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover flex-shrink-0"
                style={{ border: "3px solid #fff", boxShadow: "0 8px 24px rgba(0,0,0,0.15)" }}
              />
              <div>
                <p className="text-sm md:text-[15px] text-[var(--text)] leading-relaxed italic">
                  «&nbsp;La danse m'a permis de survivre. Arrivé en Suisse à 6 ans dans les quartiers de la Bourdonnette, la danse m'a aidé à traverser tous les défis de la vie et à devenir l'homme que je suis aujourd'hui. J'ai alors décidé de créer un lieu pour permettre à tous de se trouver par la danse. Bienvenue chez 1000 Arts&nbsp;!&nbsp;»
                </p>
                <div className="mt-2 text-xs font-semibold" style={{ color: "var(--primary)" }}>
                  Miller Sacon, fondateur de 1000 Arts
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/cours" data-testid="hero-cta-cours" className="btn-primary">
                Découvrir nos cours <ArrowRight size={16} />
              </Link>
              <Link to="/contact" data-testid="hero-cta-contact" className="btn-outline">
                Nous rencontrer
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-3 items-center text-sm">
              <a
                href="tel:+41799504031"
                data-testid="hero-phone"
                className="pulse-cta inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-base"
                style={{ background: "var(--primary)", color: "#fff", fontSize: 16 }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                Appelle-nous&nbsp;! <span className="opacity-90 hidden sm:inline">079 950 40 31</span>
              </a>
              <div className="flex flex-wrap gap-4 ml-2">
                <span className="flex items-center gap-2 text-[var(--muted)]"><Users size={16} className="text-[var(--primary)]" /> Pour tous les âges</span>
                <span className="flex items-center gap-2 text-[var(--muted)]"><Heart size={16} className="text-[var(--primary)]" /> Bienveillance</span>
                <span className="flex items-center gap-2 text-[var(--muted)]"><Sparkles size={16} className="text-[var(--primary)]" /> Épanouissement</span>
              </div>
            </div>
          </div>

          <HeroVideo
            poster="/video/1000arts-hero-poster.webp"
            sources={[{ src: "/video/1000arts-hero.mp4", type: "video/mp4" }]}
          />
        </div>
      </section>

      {/* MARQUEE */}
      <section className="marquee" data-testid="marquee">
        {[0, 1].map((k) => (
          <div className="marquee-track" key={k}>
            {MARQUEE_ITEMS.concat(MARQUEE_ITEMS).map((w, i) => (
              <span key={i} className="font-display text-3xl md:text-5xl whitespace-nowrap" style={{ color: "var(--primary)" }}>
                {w} <span style={{ color: "var(--primary-soft)" }}>·</span>
              </span>
            ))}
          </div>
        ))}
      </section>

      {/* PROCHAIN SPECTACLE — compte à rebours + places restantes (disparaît après la date) */}
      <SpectacleCountdown />

      {/* AUDIENCES */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24" data-testid="audiences">
        <div className="text-center max-w-4xl mx-auto mb-14">
          <div className="eyebrow mb-3">Pour qui ?</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight md:whitespace-nowrap">Une école <span style={{ color: "var(--primary)" }}>pour toutes et tous</span></h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {AUDIENCES.map((a) => (
            <div key={a.key} data-testid={`audience-${a.key}`} className="card group block">
              <div className="aspect-[5/3] rounded-2xl overflow-hidden mb-5 -mx-2 -mt-2">
                <img loading="lazy" decoding="async" src={thumb(a.img)} onError={fallbackToFull(a.img)} alt={a.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div>
                <div className="font-display text-2xl">{a.label}</div>
                <div className="text-sm text-[var(--muted)]">{a.sub}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-center mt-10">
          <Link to="/cours" data-testid="audiences-cta" className="btn-primary">
            Découvre nos cours <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* TRUST */}
      <section className="section-soft py-20" data-testid="trust">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="eyebrow text-center mb-3">La confiance des parents</div>
          <h2 className="font-display text-3xl md:text-4xl text-center mb-12">Une école sécurisante et reconnue</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {TRUST.map((t) => (
              <div key={t.label} className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background: "#fff", border: "1.5px solid var(--primary)" }}>
                  <t.icon size={26} style={{ color: "var(--primary)" }} />
                </div>
                <div className="font-medium text-[var(--text)]">{t.label}</div>
              </div>
            ))}
          </div>

          {/* Bloc humain — introduit l'école & renvoie vers /ecole */}
          <div
            className="mt-16 grid md:grid-cols-2 gap-10 items-center rounded-3xl p-8 md:p-12"
            style={{ background: "#fff", border: "1px solid var(--border-soft)", boxShadow: "0 10px 40px rgba(15,27,45,0.06)" }}
            data-testid="trust-human"
          >
            <div>
              <div className="eyebrow mb-3">L'ADN de l'école</div>
              <h3 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
                Une école pensée pour <span style={{ color: "var(--primary)" }}>toutes et tous</span>.
              </h3>
              <p className="font-display text-xl md:text-2xl leading-tight mt-3 text-[var(--text)] opacity-80">
                Quel que soit ton âge, ton niveau ou ton rêve.
              </p>
              <p className="mt-6 text-[var(--text)] leading-relaxed">
                Fondée par <strong>Noémie Vaney</strong> et <strong>Miller Sacon</strong>, deux artistes passionnés formés en Suisse et à New York, 1000 ARTS est née d'un rêve simple&nbsp;: créer un lieu où l'art se vit comme une célébration.
              </p>
              <p className="mt-4 text-[var(--muted)] leading-relaxed">
                Une école à taille humaine, exigeante sur la qualité artistique et infiniment bienveillante sur l'humain. Chaque élève est reconnu, encouragé et poussé à donner le meilleur de lui-même — {nbEleves || 230} élèves qui composent aujourd'hui une vraie famille.
              </p>
              <Link
                to="/ecole"
                data-testid="trust-cta-ecole"
                className="inline-flex items-center gap-2 mt-6 font-semibold hover:gap-3 transition-all"
                style={{ color: "var(--primary)" }}
              >
                Découvre l'école <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl p-6" style={{ background: "var(--primary-tint)" }}>
                <div className="font-display text-3xl md:text-4xl leading-none" style={{ color: "var(--primary)" }} data-testid="home-nb-eleves">{nbEleves || 230}</div>
                <div className="text-sm text-[var(--muted)] mt-2">élèves actifs</div>
              </div>
              <div className="rounded-2xl p-6" style={{ background: "var(--primary-tint)" }}>
                <div className="font-display text-3xl md:text-4xl leading-none" style={{ color: "var(--primary)" }}>15+</div>
                <div className="text-sm text-[var(--muted)] mt-2">disciplines enseignées</div>
              </div>
              <div className="rounded-2xl p-6" style={{ background: "var(--primary-tint)" }}>
                <div className="font-display text-3xl md:text-4xl leading-none" style={{ color: "var(--primary)" }}>2 ans</div>
                <div className="text-sm text-[var(--muted)] mt-2">âge minimum</div>
              </div>
              <div className="rounded-2xl p-6" style={{ background: "var(--primary-tint)" }}>
                <div className="font-display text-3xl md:text-4xl leading-none" style={{ color: "var(--primary)" }}>1000+</div>
                <div className="text-sm text-[var(--muted)] mt-2">spectateurs / an</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DISCIPLINES PREVIEW */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 relative overflow-x-hidden" data-testid="disciplines-preview">
        <BlobShape variant="tint" size="xl" position="top-20 -right-40" opacity={0.6} animation="drift" />
        <DotsPattern position="bottom-0 -left-10" size={220} opacity={0.1} />
        <div className="relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 reveal-on-scroll">
          <div>
            <div className="eyebrow mb-3">Disciplines</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight">Trouve <span style={{ color: "var(--primary)" }}>ton style</span></h2>
          </div>
          <Link to="/cours" data-testid="disciplines-all" className="btn-outline">Voir tous nos cours <ArrowRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {COURSES.slice(0, 8).map((c) => (
            <Link to={`/cours/${c.slug}`} key={c.slug} data-testid={`disc-${c.slug}`} className="group rounded-2xl overflow-hidden border border-[var(--border-soft)] aspect-square relative">
              <img loading="lazy" decoding="async" src={thumb(c.image)} srcSet={thumbSet(c.image)} sizes="(max-width: 768px) 50vw, 25vw" onError={fallbackToFull(c.image)} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(15,27,45,0.85), transparent 60%)" }} />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="text-white font-display text-xl">{c.name}</div>
              </div>
            </Link>
          ))}
        </div>
        </div>
      </section>

      {/* LOCAL SEO — Zone desservie (Gros-de-Vaud) */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20" data-testid="local-area" aria-labelledby="local-area-title">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="eyebrow mb-3">Gros-de-Vaud</div>
            <h2 id="local-area-title" className="font-display text-3xl md:text-4xl tracking-tight">
              Une école de danse au cœur du <span style={{ color: "var(--primary)" }}>Gros-de-Vaud</span>
            </h2>
            <p className="mt-6 text-[var(--text)] leading-relaxed">
              Installée depuis plusieurs années au <strong>Chemin des Dailles 10E à Cugy VD</strong>, 1000 ARTS accueille chaque semaine des élèves venus de tout le district. Notre studio est facilement accessible depuis <strong>Lausanne</strong> (10 minutes en voiture), <strong>Échallens</strong> (5 minutes) et rayonne sur l'ensemble du Gros-de-Vaud.
            </p>
            <p className="mt-4 text-[var(--text)] leading-relaxed">
              Nous sommes fiers d'accueillir des enfants, ados et adultes de <strong>Cugy</strong>, <strong>Étagnières</strong>, <strong>Assens</strong>, <strong>Bottens</strong>, <strong>Bretigny-sur-Morrens</strong>, <strong>Morrens</strong>, <strong>Froideville</strong>, <strong>Échallens</strong>, <strong>Villars-le-Terroir</strong>, <strong>Poliez-Pittet</strong>, <strong>Cheseaux-sur-Lausanne</strong>, <strong>Le Mont-sur-Lausanne</strong>, <strong>Sullens</strong>, <strong>Bioley-Orjulaz</strong> et de toutes les communes environnantes du Gros-de-Vaud.
            </p>
            <p className="mt-4 text-[var(--muted)] text-sm">
              Deux salles équipées (sono professionnelle, éclairage LED, parquet), parking devant l'école — viens comme tu es, on s'occupe du reste.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden aspect-[4/3]" style={{ boxShadow: "0 20px 60px rgba(15,27,45,0.15)" }}>
            <iframe
              title="Localisation 1000 ARTS à Cugy VD"
              src="https://www.google.com/maps?q=Chemin+des+Dailles+10E,+1053+Cugy+VD&output=embed"
              width="100%" height="100%" style={{ border: 0 }}
              loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* PRESSE — On parle de nous */}
      <section className="section-soft py-20" data-testid="home-press" aria-labelledby="home-press-title">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <div className="eyebrow mb-3">Dans les médias</div>
              <h2 id="home-press-title" className="font-display text-3xl md:text-4xl tracking-tight">
                On parle de <span style={{ color: "var(--primary)" }}>nous</span>
              </h2>
            </div>
            <Link to="/actualites" data-testid="home-press-all" className="inline-flex items-center gap-1 text-sm font-medium hover:opacity-80 transition-opacity" style={{ color: "var(--primary)" }}>
              Toutes nos actualités <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {PRESS.map((p) => (
              <a
                key={p.key}
                href={p.href}
                target="_blank"
                rel="noreferrer"
                data-testid={`home-press-${p.key}`}
                className="card group flex flex-col hover:-translate-y-1 transition-transform duration-300"
                style={{ padding: 0, overflow: "hidden" }}
              >
                <div className="aspect-[4/3] overflow-hidden flex items-center justify-center p-4" style={{ background: "var(--bg-soft)" }}>
                  <img
                    loading="lazy"
                    decoding="async"
                    src={thumb(p.img)}
                    onError={fallbackToFull(p.img)}
                    alt={`${p.outlet} — article sur l'école de danse 1000 ARTS à Cugy VD`}
                    className="max-h-full max-w-full object-contain rounded-lg shadow-sm group-hover:scale-[1.03] transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}>
                      <Newspaper size={12} /> {p.kicker}
                    </span>
                    <span className="text-xs text-[var(--muted)]">{p.date}</span>
                  </div>
                  <div className="font-display text-xl">{p.outlet}</div>
                  <div className="font-medium mt-1">{p.title}</div>
                  <p className="mt-3 text-sm text-[var(--muted)] leading-relaxed flex-1">{p.excerpt}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium mt-5" style={{ color: "var(--primary)" }}>
                    {p.cta} <ArrowRight size={14} />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS CAROUSEL */}
      <section className="py-20" data-testid="google-reviews">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-10 mb-12 items-center">
            <div>
              <div className="eyebrow mb-3">Témoignages</div>
              <h2 className="font-display text-3xl md:text-4xl tracking-tight">Ce que les familles<br /><span style={{ color: "var(--primary)" }}>disent de nous</span></h2>
              <div className="mt-6 flex items-center gap-3">
                <div>
                  <div className="font-display text-3xl" data-testid="google-rating">{noteGoogle.rating.toLocaleString("fr-CH", { minimumFractionDigits: 1 })}</div>
                  <div className="flex gap-0.5 mt-1">{[1,2,3,4,5].map((s) => <Star key={s} size={14} style={{ color: "#FFC107", fill: s <= Math.round(noteGoogle.rating) ? "#FFC107" : "transparent" }} />)}</div>
                </div>
                <div className="text-sm text-[var(--muted)]">basé sur<br /><strong className="text-[var(--text)]" data-testid="google-review-count">{noteGoogle.count} avis Google</strong></div>
              </div>
            </div>
            <div className="md:col-span-2"><ReviewsCarousel /></div>
          </div>
        </div>
      </section>

      {/* SPONSORS */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20" data-testid="home-sponsors" aria-labelledby="sponsors-title">
        <div className="text-center mb-12">
          <div className="eyebrow mb-3">Ils nous soutiennent</div>
          <h2 id="sponsors-title" className="font-display text-3xl md:text-4xl tracking-tight">Nos sponsors</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 items-stretch max-w-6xl mx-auto">
          <a href="https://epizzeria.ch" target="_blank" rel="noopener noreferrer" data-testid="sponsor-epizzeria" className="flex items-center justify-center h-40 rounded-3xl border border-[var(--border-soft)] bg-white p-6 hover:shadow-lg hover:-translate-y-1 transition-all">
            <img loading="lazy" decoding="async" src={thumb("/img/c45588f25c.webp")} onError={fallbackToFull("/img/c45588f25c.webp")} alt="Épizzeria — pizzeria, épicerie, traiteur" className="max-h-full max-w-full object-contain" />
          </a>
          <a href="https://mobiliere.ch" target="_blank" rel="noopener noreferrer" data-testid="sponsor-mobiliere" className="flex items-center justify-center h-40 rounded-3xl border border-[var(--border-soft)] bg-white p-6 hover:shadow-lg hover:-translate-y-1 transition-all">
            <img loading="lazy" decoding="async" src={thumb("/img/e1b5642faa.webp")} onError={fallbackToFull("/img/e1b5642faa.webp")} alt="La Mobilière — Agence Échallens, Hilson Ferro" className="max-h-full max-w-full object-contain" />
          </a>
          <a href="https://garagepaquier.ch" target="_blank" rel="noopener noreferrer" data-testid="sponsor-paquier" className="flex items-center justify-center h-40 rounded-3xl border border-[var(--border-soft)] bg-white p-6 hover:shadow-lg hover:-translate-y-1 transition-all">
            <img loading="lazy" decoding="async" src={thumb("/img/a47e4e39b8.webp")} onError={fallbackToFull("/img/a47e4e39b8.webp")} alt="Garage Paquier Échallens" className="max-h-full max-w-full object-contain" />
          </a>
          <a href="https://atix-immobilier.ch/" target="_blank" rel="noopener noreferrer" data-testid="sponsor-atix" className="flex items-center justify-center h-40 rounded-3xl border border-[var(--border-soft)] bg-white p-6 hover:shadow-lg hover:-translate-y-1 transition-all">
            <img loading="lazy" decoding="async" src={thumb("/img/atix-immobilier.webp")} onError={fallbackToFull("/img/atix-immobilier.webp")} alt="ATIX Immobilier — La Signature ATIX" className="max-h-full max-w-full object-contain" />
          </a>
        </div>
        <div className="text-center mt-12">
          <Link to="/sponsoring" data-testid="sponsor-cta" className="btn-primary" style={{ padding: "14px 28px" }}>
            Découvre nos offres de sponsoring <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24">
        <div className="card-flat text-center" style={{ background: "var(--primary)", color: "#fff", borderColor: "transparent", padding: "60px 32px" }}>
          <h2 className="font-display text-4xl md:text-5xl">Prêt·e à essayer&nbsp;?</h2>
          <p className="mt-4 text-white/90 max-w-xl mx-auto">Réserve ta place dès maintenant et viens nous rencontrer.</p>
          <Link to="/inscription" data-testid="home-final-cta" className="inline-flex items-center gap-2 mt-8 px-8 py-4 rounded-full bg-white text-[var(--primary)] font-semibold">
            S'inscrire <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function HolidayBanner() {
  const active = getActiveBreak();
  if (!active) return null;
  const start = active.startEcole || active.start;
  const end = active.endEcole || active.end;
  const fmt = (iso) => {
    const [y, m, d] = iso.split("-");
    return `${d}.${m}.${y}`;
  };
  return (
    <div className="fixed top-0 left-0 right-0 z-40 pt-[76px] pointer-events-none">
      <div
        data-testid="holiday-banner"
        className="pointer-events-auto mx-auto max-w-[1400px] mt-2 px-6 md:px-12"
      >
        <div
          className="rounded-2xl px-5 py-3 flex items-start gap-3 shadow-md"
          style={{ background: "#FFF7ED", borderLeft: "4px solid #F97316", color: "#7C2D12" }}
        >
          <span className="text-xl leading-none">🌴</span>
          <div className="text-sm leading-relaxed">
            <strong>École fermée — {active.label}</strong>
            <span className="hidden sm:inline"> · du <strong>{fmt(start)}</strong> au <strong>{fmt(end)}</strong>. Reprise des cours dès le lendemain.</span>
            <span className="sm:hidden"> · reprise le {fmt(end)}.</span>
          </div>
        </div>
      </div>
    </div>
  );
}


function HeroVideo({ sources, poster }) {
  const [muted, setMuted] = useState(true);
  const [sonRefuse, setSonRefuse] = useState(false);
  const videoRef = useRef(null);

  const lancer = () => {
    const v = videoRef.current;
    if (v) v.play().catch(() => {});
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    // React n'écrit pas toujours l'attribut `muted` dans le HTML ; or Safari iOS le lit AVANT
    // de décider s'il autorise la lecture automatique. Sans ça, iOS refuse de démarrer et
    // affiche son propre bouton « play » par-dessus l'affiche.
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute("muted", "");
    lancer();
    const retry = setTimeout(lancer, 1200);
    // Un navigateur peut mettre la vidéo en pause de lui-même (onglet en arrière-plan,
    // économie de batterie, son refusé) : on relance, mais toujours en muet — sinon on
    // s'acharnerait contre un refus et la vidéo resterait figée.
    const onPause = () => {
      if (v.ended) return;
      v.muted = true;
      setMuted(true);
      v.play().catch(() => {});
    };
    v.addEventListener("pause", onPause);
    return () => { clearTimeout(retry); v.removeEventListener("pause", onPause); };
  }, []);

  const toggleMute = async (e) => {
    e?.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    if (!next) v.volume = 1;
    setMuted(next);
    try {
      // Le clic est une interaction utilisateur : c'est le seul moment où le navigateur
      // accepte le son. On attend vraiment la réponse — l'ancien code testait `v.paused`
      // dans l'instant, alors que la mise en pause arrive une fraction de seconde plus tard,
      // et la vidéo restait bloquée sur une image.
      await v.play();
      setSonRefuse(false);
    } catch {
      v.muted = true;
      setMuted(true);
      setSonRefuse(true);
      v.play().catch(() => {});
    }
  };

  return (
    <div
      className="relative rounded-[32px] overflow-hidden shadow-2xl aspect-[4/5] lg:aspect-[5/6] bg-black"
      data-testid="hero-video-container"
      onClick={lancer}
    >
      <video
        ref={videoRef}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        disablePictureInPicture
        aria-label="1000 ARTS — cours de danse à Cugy VD"
        className="hero-video absolute inset-0 w-full h-full object-cover"
        style={{ pointerEvents: "none" }}
      >
        {/* Un seul format : le H.264/AAC est lu par tous les navigateurs et tous les
            téléphones. Un WebM serait à peine plus léger et alourdirait la maintenance. */}
        {sources.map((s) => (
          <source key={s.src} src={s.src} type={s.type} />
        ))}
      </video>
      <button
        type="button"
        onClick={toggleMute}
        aria-label={muted ? "Activer le son" : "Couper le son"}
        data-testid="hero-video-mute-toggle"
        className="absolute top-4 right-4 z-10 w-12 h-12 rounded-full flex items-center justify-center shadow-lg backdrop-blur-md transition-transform hover:scale-110 active:scale-95"
        style={{ background: "rgba(255,255,255,0.95)", color: "var(--primary)" }}
      >
        {muted ? <VolumeX size={22} /> : <Volume2 size={22} />}
      </button>
      {sonRefuse && (
        <div
          data-testid="hero-video-sound-blocked"
          className="absolute top-4 left-4 right-20 z-10 text-xs font-medium px-3 py-2 rounded-xl"
          style={{ background: "rgba(15,27,45,0.85)", color: "#fff" }}
        >
          Ton navigateur bloque le son — touche à nouveau l'icône pour l'autoriser.
        </div>
      )}
    </div>
  );
}
